
export interface TimeRange {
  start_time: string;
  end_time: string;
}

export interface BookingTimeRange {
  booking_date: string;
  start_time: string;
  end_time: string;
  room_id?: string;
}

export interface BookedRangeWithCapacity {
  start_time: string;
  end_time: string;
  room_id: string | null;
  room_capacity: number;
}

/**
 * Check if two time ranges overlap
 * Times should be in HH:MM:SS format
 */
export const doTimeRangesOverlap = (range1: TimeRange, range2: TimeRange): boolean => {
  // Create Date objects using the same base date to compare times accurately
  const baseDate = '1970-01-01';
  const start1 = new Date(`${baseDate}T${range1.start_time}`);
  const end1 = new Date(`${baseDate}T${range1.end_time}`);
  const start2 = new Date(`${baseDate}T${range2.start_time}`);
  const end2 = new Date(`${baseDate}T${range2.end_time}`);

  // Two ranges overlap if one starts before the other ends and vice versa
  return start1 < end2 && start2 < end1;
};

/**
 * Check if two booking time ranges overlap (including date and room)
 */
export const doBookingTimeRangesOverlap = (
  booking1: BookingTimeRange,
  booking2: BookingTimeRange
): boolean => {
  // Must be same date
  if (booking1.booking_date !== booking2.booking_date) {
    return false;
  }

  // Check room compatibility - if both have rooms, they must be the same to conflict
  // If either has no room, they can conflict on time overlap
  const roomConflict = !booking1.room_id || 
                       !booking2.room_id || 
                       booking1.room_id === booking2.room_id;

  if (!roomConflict) {
    return false;
  }

  // Check time overlap
  const timeOverlap = doTimeRangesOverlap(
    { start_time: booking1.start_time, end_time: booking1.end_time },
    { start_time: booking2.start_time, end_time: booking2.end_time }
  );

  return timeOverlap;
};

/**
 * Create a datetime string from date and time components
 */
export const createDateTime = (date: string, time: string): string => {
  return `${date}T${time}`;
};

/**
 * Count overlapping bookings for a proposed time slot in a specific room
 * This is used for capacity-aware conflict detection
 */
export const countOverlappingBookings = (
  proposedSlot: TimeRange & { room_id?: string | null },
  bookedRanges: BookedRangeWithCapacity[],
): number => {
  return bookedRanges.filter(booking => {
    // Check room compatibility
    // If proposed slot has no room, count all overlapping bookings
    // If booking has no room, it's a potential conflict
    // If both have rooms, they must match
    const roomConflict = !proposedSlot.room_id || 
                         !booking.room_id || 
                         proposedSlot.room_id === booking.room_id;
    
    if (!roomConflict) {
      return false;
    }

    // Check time overlap
    return doTimeRangesOverlap(
      { start_time: proposedSlot.start_time, end_time: proposedSlot.end_time },
      { start_time: booking.start_time, end_time: booking.end_time }
    );
  }).length;
};

/**
 * Check if a time slot overlaps with any existing bookings
 * Updated to use the new conflict detection logic
 */
export const hasOverlapWithBookings = (
  timeSlot: TimeRange & { room_id?: string },
  bookings: BookingTimeRange[],
  date: string
): boolean => {
  const slotBooking: BookingTimeRange = {
    booking_date: date,
    start_time: timeSlot.start_time,
    end_time: timeSlot.end_time,
    room_id: timeSlot.room_id,
  };

  const hasOverlap = bookings.some(booking => {
    return doBookingTimeRangesOverlap(slotBooking, booking);
  });

  return hasOverlap;
};
